/**
 * Inventory Page
 * Stock management and adjustments
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  Pagination,
  SearchInput,
  Button,
} from '../../components/ui/index.js';
import { inventoryService } from '../../services/inventory.js';
import type { ProductStock, StockAdjustment, AdjustmentType, AdjustmentReason, StockLevelsParams } from '../../types/inventory.js';

const ADJUSTMENT_REASONS: { value: AdjustmentReason; label: string }[] = [
  { value: 'purchase', label: 'Purchase' },
  { value: 'sale', label: 'Sale' },
  { value: 'return', label: 'Return' },
  { value: 'damage', label: 'Damage' },
  { value: 'theft', label: 'Theft' },
  { value: 'correction', label: 'Correction' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'production', label: 'Production' },
  { value: 'consumption', label: 'Consumption' },
  { value: 'expired', label: 'Expired' },
  { value: 'other', label: 'Other' },
];

type TabType = 'stock' | 'adjustments' | 'low-stock';

export function InventoryPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<TabType>('stock');
  const [params, setParams] = useState<StockLevelsParams>({ page: 1, limit: 20 });
  const [search, setSearch] = useState('');
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductStock | null>(null);
  const [adjustmentForm, setAdjustmentForm] = useState({
    adjustmentType: 'add' as AdjustmentType,
    quantity: '',
    reason: 'purchase' as AdjustmentReason,
    notes: '',
  });

  // Queries
  const { data: summary } = useQuery({
    queryKey: ['inventory-summary'],
    queryFn: () => inventoryService.getSummary(),
  });

  const { data: stockData, isLoading: stockLoading } = useQuery({
    queryKey: ['inventory-stock', params, search],
    queryFn: () => inventoryService.getStockLevels({ ...params, search: search || undefined }),
    enabled: activeTab === 'stock',
  });

  const { data: lowStockData, isLoading: lowStockLoading } = useQuery({
    queryKey: ['inventory-low-stock', params],
    queryFn: () => inventoryService.getLowStock({ page: params.page, limit: params.limit }),
    enabled: activeTab === 'low-stock',
  });

  const { data: adjustmentsData, isLoading: adjustmentsLoading } = useQuery({
    queryKey: ['inventory-adjustments', params],
    queryFn: () => inventoryService.getAdjustments({ page: params.page, limit: params.limit }),
    enabled: activeTab === 'adjustments',
  });

  // Mutations
  const adjustmentMutation = useMutation({
    mutationFn: (data: { productId: string; adjustmentType: AdjustmentType; quantity: number; reason: AdjustmentReason; notes?: string }) =>
      inventoryService.createAdjustment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory-stock'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-summary'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-adjustments'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-low-stock'] });
      setShowAdjustmentModal(false);
      resetAdjustmentForm();
    },
  });

  const resetAdjustmentForm = () => {
    setSelectedProduct(null);
    setAdjustmentForm({
      adjustmentType: 'add',
      quantity: '',
      reason: 'purchase',
      notes: '',
    });
  };

  const handleAdjustment = (product: ProductStock) => {
    setSelectedProduct(product);
    setShowAdjustmentModal(true);
  };

  const submitAdjustment = () => {
    if (!selectedProduct || !adjustmentForm.quantity) return;
    adjustmentMutation.mutate({
      productId: selectedProduct.id,
      adjustmentType: adjustmentForm.adjustmentType,
      quantity: parseFloat(adjustmentForm.quantity),
      reason: adjustmentForm.reason,
      notes: adjustmentForm.notes || undefined,
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const tabs: { key: TabType; label: string }[] = [
    { key: 'stock', label: 'Stock Levels' },
    { key: 'adjustments', label: 'Adjustments' },
    { key: 'low-stock', label: 'Low Stock Alerts' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('inventory.title', 'Inventory')}</h1>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Tracked Products</p>
                <p className="text-2xl font-semibold text-gray-900">{summary?.trackedProducts ?? '-'}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Low Stock Items</p>
                <p className="text-2xl font-semibold text-orange-600">{summary?.lowStockCount ?? '-'}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-lg">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Out of Stock</p>
                <p className="text-2xl font-semibold text-red-600">{summary?.outOfStockCount ?? '-'}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Stock Value</p>
                <p className="text-2xl font-semibold text-green-600">{summary ? formatCurrency(summary.totalStockValue) : '-'}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setActiveTab(tab.key);
                setParams({ page: 1, limit: 20 });
              }}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
              {tab.key === 'low-stock' && summary?.lowStockCount ? (
                <span className="ml-2 bg-orange-100 text-orange-600 py-0.5 px-2 rounded-full text-xs">
                  {summary.lowStockCount}
                </span>
              ) : null}
            </button>
          ))}
        </nav>
      </div>

      {/* Stock Levels Tab */}
      {activeTab === 'stock' && (
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <CardTitle>Stock Levels</CardTitle>
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search products..."
              />
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">In Stock</TableHead>
                  <TableHead className="text-right">Reserved</TableHead>
                  <TableHead className="text-right">Available</TableHead>
                  <TableHead className="text-right">Reorder Level</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stockLoading ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-8 text-center text-sm text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : !stockData?.items.length ? (
                  <TableEmpty colSpan={9} message="No products found" />
                ) : (
                  stockData.items.map((product: ProductStock) => (
                    <TableRow key={product.id}>
                      <TableCell className="font-medium">{product.name}</TableCell>
                      <TableCell className="text-gray-500">{product.sku || '-'}</TableCell>
                      <TableCell>{product.categoryName || '-'}</TableCell>
                      <TableCell className="text-right">{product.stockQuantity}</TableCell>
                      <TableCell className="text-right">{product.reservedQuantity}</TableCell>
                      <TableCell className="text-right font-medium">{product.availableQuantity}</TableCell>
                      <TableCell className="text-right">{product.reorderLevel ?? '-'}</TableCell>
                      <TableCell>
                        {product.stockQuantity <= 0 ? (
                          <Badge variant="error">Out of Stock</Badge>
                        ) : product.isLowStock ? (
                          <Badge variant="warning">Low Stock</Badge>
                        ) : (
                          <Badge variant="success">In Stock</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAdjustment(product)}
                        >
                          Adjust
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            {stockData && stockData.totalPages > 1 && (
              <div className="mt-4">
                <Pagination
                  page={stockData.page}
                  totalPages={stockData.totalPages}
                  total={stockData.total}
                  limit={stockData.limit}
                  onPageChange={(page) => setParams({ ...params, page })}
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Low Stock Tab */}
      {activeTab === 'low-stock' && (
        <Card>
          <CardHeader>
            <CardTitle>Low Stock Alerts</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Current Stock</TableHead>
                  <TableHead className="text-right">Reorder Level</TableHead>
                  <TableHead className="text-right">Suggested Order</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lowStockLoading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-sm text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : !lowStockData?.items.length ? (
                  <TableEmpty colSpan={7} message="No low stock items" />
                ) : (
                  lowStockData.items.map((product: ProductStock) => (
                    <TableRow key={product.id}>
                      <TableCell className="font-medium">{product.name}</TableCell>
                      <TableCell className="text-gray-500">{product.sku || '-'}</TableCell>
                      <TableCell>{product.categoryName || '-'}</TableCell>
                      <TableCell className="text-right text-orange-600 font-medium">
                        {product.stockQuantity}
                      </TableCell>
                      <TableCell className="text-right">{product.reorderLevel ?? '-'}</TableCell>
                      <TableCell className="text-right text-blue-600 font-medium">
                        {product.reorderQuantity ?? '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAdjustment(product)}
                        >
                          Restock
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            {lowStockData && lowStockData.totalPages > 1 && (
              <div className="mt-4">
                <Pagination
                  page={lowStockData.page}
                  totalPages={lowStockData.totalPages}
                  total={lowStockData.total}
                  limit={lowStockData.limit}
                  onPageChange={(page) => setParams({ ...params, page })}
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Adjustments Tab */}
      {activeTab === 'adjustments' && (
        <Card>
          <CardHeader>
            <CardTitle>Stock Adjustments History</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                  <TableHead className="text-right">Before</TableHead>
                  <TableHead className="text-right">After</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {adjustmentsLoading ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-8 text-center text-sm text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : !adjustmentsData?.items.length ? (
                  <TableEmpty colSpan={8} message="No adjustments found" />
                ) : (
                  adjustmentsData.items.map((adj: StockAdjustment) => (
                    <TableRow key={adj.id}>
                      <TableCell className="text-gray-500">
                        {new Date(adj.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="font-medium">{adj.productName}</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            adj.adjustmentType === 'add'
                              ? 'success'
                              : adj.adjustmentType === 'remove'
                              ? 'error'
                              : 'default'
                          }
                        >
                          {adj.adjustmentType}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {adj.adjustmentType === 'add' ? '+' : adj.adjustmentType === 'remove' ? '-' : ''}
                        {adj.quantity}
                      </TableCell>
                      <TableCell className="text-right text-gray-500">{adj.quantityBefore}</TableCell>
                      <TableCell className="text-right">{adj.quantityAfter}</TableCell>
                      <TableCell className="capitalize">{adj.reason}</TableCell>
                      <TableCell className="text-gray-500">{adj.createdByName || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            {adjustmentsData && adjustmentsData.totalPages > 1 && (
              <div className="mt-4">
                <Pagination
                  page={adjustmentsData.page}
                  totalPages={adjustmentsData.totalPages}
                  total={adjustmentsData.total}
                  limit={adjustmentsData.limit}
                  onPageChange={(page) => setParams({ ...params, page })}
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Adjustment Modal */}
      {showAdjustmentModal && selectedProduct && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-25" onClick={() => setShowAdjustmentModal(false)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold mb-4">Adjust Stock: {selectedProduct.name}</h3>
              <p className="text-sm text-gray-500 mb-4">
                Current stock: <span className="font-medium">{selectedProduct.stockQuantity}</span>
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adjustment Type
                  </label>
                  <select
                    value={adjustmentForm.adjustmentType}
                    onChange={(e) =>
                      setAdjustmentForm({ ...adjustmentForm, adjustmentType: e.target.value as AdjustmentType })
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2"
                  >
                    <option value="add">Add Stock</option>
                    <option value="remove">Remove Stock</option>
                    <option value="set">Set Quantity</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={adjustmentForm.quantity}
                    onChange={(e) => setAdjustmentForm({ ...adjustmentForm, quantity: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2"
                    placeholder="Enter quantity"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason
                  </label>
                  <select
                    value={adjustmentForm.reason}
                    onChange={(e) =>
                      setAdjustmentForm({ ...adjustmentForm, reason: e.target.value as AdjustmentReason })
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2"
                  >
                    {ADJUSTMENT_REASONS.map((reason) => (
                      <option key={reason.value} value={reason.value}>
                        {reason.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={adjustmentForm.notes}
                    onChange={(e) => setAdjustmentForm({ ...adjustmentForm, notes: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2"
                    rows={3}
                    placeholder="Add any notes..."
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAdjustmentModal(false);
                    resetAdjustmentForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={submitAdjustment}
                  disabled={!adjustmentForm.quantity || adjustmentMutation.isPending}
                >
                  {adjustmentMutation.isPending ? 'Saving...' : 'Save Adjustment'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InventoryPage;
